import type { TrashedDocument } from "@models";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPremium,
  useGridApiContext,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import { DraftIcon } from "src/components/DocumentStatusFileIcons";
import { DocViewerMoreOptions } from "src/components/DocViewer/components/docViewerTopBar/actions/DocViewerMoreOptions";
import { getUserDisplayName } from "src/utils/user";
import { DocumentScopeChip } from "./DocumentScopeChip";
import { formatDocumentTableDate } from "./DocumentsTableDates";

const DAY_MS = 24 * 60 * 60 * 1000;

const getTrashLocation = (document: TrashedDocument) =>
  [
    document.device?.name,
    document.organization.name,
    document.folder?.name ?? "Document root",
  ]
    .filter(Boolean)
    .join(" · ");

const getTrashDeletedBy = (document: TrashedDocument) =>
  document.softDeletionEvent.createdByUser
    ? getUserDisplayName(document.softDeletionEvent.createdByUser)
    : "Deleted user";

type DocumentTrashTableProps = {
  documents: TrashedDocument[];
  isLoading: boolean;
  isMutating: boolean;
  onEmptyTrash: () => void;
  onOpenDocument: (document: TrashedDocument) => void;
  onPermanentlyDelete: (document: TrashedDocument) => void;
  onRestore: (document: TrashedDocument) => void;
};

const ExpiryChip = ({ document }: { document: TrashedDocument }) => {
  const daysLeft = Math.max(
    0,
    Math.ceil(
      (document.softDeletionEvent.purgeAfter.getTime() - Date.now()) / DAY_MS
    )
  );
  if (daysLeft > 7) {
    return null;
  }

  return (
    <Chip
      color="error"
      label={`${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
      size="small"
      variant="outlined"
      sx={{ height: 24 }}
    />
  );
};

const renderTextCell = ({
  formattedValue,
  value,
}: GridRenderCellParams<TrashedDocument>) => {
  const text = String(formattedValue ?? value ?? "");
  return (
    <Typography component="span" noWrap title={text} variant="body2">
      {text}
    </Typography>
  );
};

const stopRowEvent = (event: React.MouseEvent) => event.stopPropagation();

const TrashToolbar = () => {
  const apiRef = useGridApiContext();
  const [search, setSearch] = useState("");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    apiRef.current.setQuickFilterValues(value.trim() ? [value.trim()] : []);
  };

  return (
    <Stack
      data-testid="trash-toolbar"
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        height: 42,
        px: "10px",
      }}
    >
      <TextField
        fullWidth
        hiddenLabel
        inputProps={{ "aria-label": "Search Trash" }}
        onChange={(event) => handleSearchChange(event.target.value)}
        placeholder="Search"
        size="small"
        value={search}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon
                aria-hidden="true"
                sx={{ color: "text.secondary", fontSize: 16 }}
              />
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
};

export const DocumentTrashTable = ({
  documents,
  isLoading,
  isMutating,
  onEmptyTrash,
  onOpenDocument,
  onPermanentlyDelete,
  onRestore,
}: DocumentTrashTableProps) => {
  const columns = useMemo<GridColDef<TrashedDocument>[]>(
    () => [
      {
        field: "title",
        headerName: "Name",
        flex: 38,
        minWidth: 280,
        getApplyQuickFilterFn: (value) => {
          const search = String(value).toLocaleLowerCase();
          return (_cellValue, row) =>
            [
              row.title,
              row.id,
              getTrashLocation(row),
              getTrashDeletedBy(row),
            ].some((text) => text.toLocaleLowerCase().includes(search));
        },
        renderCell: ({ row }) => (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1.5,
              minWidth: 0,
              width: "100%",
            }}
          >
            <DraftIcon aria-hidden="true" sx={{ flex: "none" }} />
            <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
              <Typography
                data-testid="trash-table-name"
                noWrap
                title={row.title}
                variant="body2"
              >
                {row.title}
              </Typography>
              <DocumentScopeChip
                label={row.device?.name ?? "Global"}
                maxWidth={150}
                tooltipTitle={`Original location: ${getTrashLocation(row)}`}
              />
              <ExpiryChip document={row} />
            </Stack>
          </Box>
        ),
      },
      {
        field: "deletedBy",
        headerName: "Deleted by",
        flex: 17,
        minWidth: 150,
        renderCell: renderTextCell,
        valueGetter: (_value, row) => getTrashDeletedBy(row),
      },
      {
        field: "deletedAt",
        flex: 14,
        headerName: "Deleted at",
        minWidth: 130,
        renderCell: ({ value }) => {
          const formattedValue = formatDocumentTableDate(value);
          return (
            <Typography component="span" noWrap title={formattedValue}>
              {formattedValue}
            </Typography>
          );
        },
        type: "dateTime",
        valueGetter: (_value, row) => row.softDeletionEvent.createdAt,
        valueFormatter: (value) => formatDocumentTableDate(value),
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        disableExport: true,
        filterable: false,
        headerAlign: "right",
        sortable: false,
        width: 56,
        renderCell: ({ row }) => (
          <Box
            onClick={stopRowEvent}
            onMouseDown={stopRowEvent}
            sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
          >
            <DocViewerMoreOptions
              dangerZoneButtons={[
                {
                  title: "Delete forever",
                  onClick: () => onPermanentlyDelete(row),
                },
              ]}
              dataTestId={`trash-actions-${row.id}`}
              hideTriggerTooltip={false}
              isDisabled={isMutating}
              menuId={`trash-actions-menu-${row.id}`}
              normalButtons={[
                {
                  title: "Restore",
                  onClick: () => onRestore(row),
                },
              ]}
              triggerAriaLabel={`More actions for ${row.title}`}
              triggerIcon={
                <MoreVertIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              }
              triggerTooltipTitle="More actions"
            />
          </Box>
        ),
      },
    ],
    [isMutating, onPermanentlyDelete, onRestore]
  );

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        gap={1}
        sx={{
          backgroundColor: "rgba(211, 47, 47, 0.06)",
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 44,
          px: "10.5px",
          py: 0.5,
        }}
      >
        <InfoOutlinedIcon color="error" sx={{ fontSize: 18 }} />
        <Typography
          color="text.secondary"
          fontSize={13}
          lineHeight="18px"
          sx={{ flex: 1 }}
        >
          Documents in Trash are recoverable for 30 days, then deleted
          permanently. These files are not available in search, dashboards, or
          training while in the Trash.
        </Typography>
        {documents.length > 0 && (
          <Button
            color="error"
            disabled={isMutating}
            onClick={onEmptyTrash}
            size="small"
            sx={{ fontSize: 11, height: 24, minWidth: 0, px: 0.75 }}
            variant="contained"
          >
            Empty Trash
          </Button>
        )}
      </Stack>

      <DataGridPremium
        aria-label="Document Trash"
        columnBufferPx={2000}
        columnHeaderHeight={42}
        columns={columns}
        disableColumnMenu
        disableColumnReorder
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        hideFooter
        loading={isLoading}
        onRowClick={({ row }) => onOpenDocument(row)}
        rows={documents}
        scrollbarSize={12}
        slots={{ toolbar: TrashToolbar }}
        sx={{
          border: 0,
          flex: 1,
          fontSize: 14,
          minHeight: 240,
          "& .MuiDataGrid-cell": { px: "10px", py: "9px" },
          "& .MuiDataGrid-columnHeader": { px: "10px" },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
          ".MuiDataGrid-row": { cursor: "pointer" },
          ".MuiDataGrid-virtualScroller": { overflowX: "auto" },
        }}
      />
    </Box>
  );
};
